import { Link } from "react-router-dom";
import { Bed, Bathtub, Ruler, MapPin, Heart } from "@phosphor-icons/react";
import { formatListingPrice } from "@/lib/constants";
import useFavorites from "@/hooks/useFavorites";
import ListingImage from "@/components/ListingImage";
import PropertyContextBadges from "@/components/PropertyContextBadges";

const STATUS_LABEL = {
  active: { text: "For Sale", cls: "text-brand border-brand/40 bg-brand/10" },
  pending: { text: "Pending", cls: "text-blue-300 border-blue-400/30 bg-blue-400/10" },
  sold: { text: "Sold", cls: "text-white/50 border-white/20 bg-white/5" },
};

export default function PropertyCard({ property }) {
  const isRent = property.listing_type === "rent";
  const s = isRent
    ? { text: "For Rent", cls: "text-brand border-brand/40 bg-brand/10" }
    : (STATUS_LABEL[property.status] || STATUS_LABEL.active);
  const { isFavorite, toggle } = useFavorites();
  const saved = isFavorite(property.id);
  const isLive = property.source === "borough";
  const bedsLabel = property.bedrooms === 0 ? "Studio" : property.bedrooms;
  return (
    <Link
      to={`/listings/${property.id}`}
      data-testid={`property-card-${property.id}`}
      className="group block bg-[#101116] border border-white/5 rounded-sm overflow-hidden hover:border-brand/40 transition-colors duration-300"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <ListingImage
          src={property.images?.[0]}
          alt={property.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <span className={`absolute top-3 left-3 px-3 py-1 text-[11px] uppercase tracking-wider rounded-sm border ${s.cls}`}>
          {s.text}
        </span>
        {isLive && (
          <span
            data-testid={`live-badge-${property.id}`}
            className="absolute top-3 left-24 px-2.5 py-1 text-[10px] uppercase tracking-wider rounded-sm border border-white/25 bg-black/45 text-white/80"
          >
            Live NYC
          </span>
        )}
        {(property.near_transit || property.walk_in || property.no_fee || property.months_free || property.pets_allowed) && (
          <div className="absolute bottom-12 left-3 flex flex-wrap gap-1.5 max-w-[85%]">
            {property.no_fee && (
              <span
                data-testid={`no-fee-badge-${property.id}`}
                className="px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-brand/90 text-black border border-brand"
              >
                No fee
              </span>
            )}
            {property.months_free ? (
              <span
                data-testid={`months-free-badge-${property.id}`}
                className="px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-black/55 text-white/85 border border-white/15"
              >
                {Number(property.months_free)} mo free
              </span>
            ) : null}
            {property.pets_allowed && (
              <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-black/55 text-white/85 border border-white/15">
                Pets OK
              </span>
            )}
            {property.near_transit && (
              <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-black/55 text-white/85 border border-white/15">
                Near transit
              </span>
            )}
            {property.walk_in && (
              <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-black/55 text-white/85 border border-white/15">
                Walk-in
              </span>
            )}
          </div>
        )}
        <button
          type="button"
          data-testid={`favorite-toggle-${property.id}`}
          aria-label={saved ? "Remove from saved" : "Save home"}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(property.id); }}
          className={`absolute top-3 right-3 flex h-9 w-9 items-center justify-center rounded-full backdrop-blur-sm transition-colors ${
            saved ? "bg-brand text-black" : "bg-black/40 text-white hover:bg-black/60"
          }`}
        >
          <Heart size={18} weight={saved ? "fill" : "regular"} />
        </button>
        <span className="absolute bottom-3 left-3 text-2xl font-serif text-white drop-shadow">
          {formatListingPrice(property.price, property.listing_type)}
        </span>
      </div>
      <div className="p-5">
        <h3 className="text-white text-lg leading-snug mb-1 line-clamp-1">{property.title}</h3>
        <p className="flex items-center gap-1.5 text-xs text-white/40 mb-4">
          <MapPin size={13} weight="duotone" className="text-brand shrink-0" />
          <span className="line-clamp-2">{property.address}</span>
        </p>
        <div className="flex items-center gap-5 text-sm text-white/60">
          <span className="flex items-center gap-1.5"><Bed size={16} weight="duotone" /> {bedsLabel}</span>
          <span className="flex items-center gap-1.5"><Bathtub size={16} weight="duotone" /> {property.bathrooms}</span>
          {property.sqft ? (
            <span className="flex items-center gap-1.5"><Ruler size={16} weight="duotone" /> {property.sqft.toLocaleString()} sf</span>
          ) : null}
        </div>
        {property.days_on_market != null && (
          <p className="mt-3 text-[11px] uppercase tracking-wider text-white/30">
            {property.days_on_market === 0 ? "Just listed" : `${property.days_on_market} days on market`}
          </p>
        )}
        <PropertyContextBadges property={property} compact className="mt-3" />
      </div>
    </Link>
  );
}
